import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useToast } from '@pairy/pds-react';

import useCheckEmailAuthCode from '@/hooks/mutations/auth/useCheckEmailAuthCode';
import useMakeUserEmailAuthCode from '@/hooks/mutations/auth/useMakeUserEmailAuthCode';

import useHandleTimer from './useHandleTimer';

const VALID_TIME = 30;

const useCheckUserEmail = () => {
  const [authCode, setAuthCode] = useState('');
  const [isAuthCodeInputVisible, setIsAuthCodeInputVisible] = useState(false);
  const [isAuthCodeInputEditable, setIsAuthCodeInputEditable] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [isAuthCodeChecked, setIsAuthCodeChecked] = useState(false);
  const [authButtonMessage, setAuthButtonMessage] =
    useState('입력한 이메일로 인증코드 받기');

  const toast = useToast();

  const { mutate: makeUserEmailCode } = useMakeUserEmailAuthCode();
  const { mutate: checkEmailAuthCode } = useCheckEmailAuthCode();
  const { time, handleTimerStart, handleTimerEnd, handleTimerInit } =
    useHandleTimer({
      totalTime: VALID_TIME * 60,
    });

  const isAuthCodeClickable = useMemo(() => {
    return isAuthCodeInputEditable && authCode.length === 6;
  }, [isAuthCodeInputEditable, authCode]);

  const [minutes, seconds] = useMemo(() => {
    return [
      Math.floor(time / 60),
      Math.floor(time % 60) === 0 ? '00' : Math.floor(time % 60),
    ];
  }, [time]);

  const handleGetCodeButtonClick = (email: string) => {
    makeUserEmailCode(
      { email },
      {
        onSuccess: () => {
          setIsAuthCodeInputVisible(true);
          setIsAuthCodeInputEditable(true);
          setIsAuthCodeChecked(false);
          setAuthButtonMessage('다시 메일로 인증코드 받기');
          handleTimerInit();
          handleTimerStart();
          toast('입력하신 이메일로 인증번호를 보냈습니다!');
        },
        onError: (error) => {
          if (axios.isAxiosError(error) && error.response?.data) {
            const { status } = error.response;
            if (status === 409) {
              toast.error(
                '이미 가입한 이메일입니다. 다른 계정으로 시도해주세요',
              );
            }
          }
        },
      },
    );
  };

  const handleAuthCodeConfirmClick = () => {
    if (!isNaN(Number(authCode)) && authCode.length === 6) {
      checkEmailAuthCode(
        { code: Number(authCode) },
        {
          onSuccess: () => {
            setIsAuthCodeChecked(true);
            setIsAuthCodeInputVisible(false);
            setAuthButtonMessage('이메일 인증 완료');
            handleTimerEnd();
          },
          onError: (error) => {
            if (axios.isAxiosError(error) && error.response?.data) {
              const { status } = error.response;
              if (status === 400) {
                setErrorMessage('코드가 일치하지 않습니다');
                return;
              }
              if (status === 410) {
                setErrorMessage('코드가 만료되었습니다');
              }
            }
          },
        },
      );
    }
  };

  useEffect(() => {
    if (time === 0 && !isAuthCodeChecked) {
      setIsAuthCodeInputEditable(false);
      toast.error(
        '이메일 인증 시간이 초과되었습니다! 인증코드 받기 버튼을 다시 클릭해주세요!',
      );
    }
  }, [time]);

  return {
    handleAuthCodeConfirmClick,
    handleGetCodeButtonClick,
    isAuthCodeInputVisible,
    isAuthCodeChecked,
    isAuthCodeInputEditable,
    isAuthCodeClickable,
    authCode,
    time,
    minutes,
    seconds,
    errorMessage,
    authButtonMessage,
    setAuthCode,
  };
};

export default useCheckUserEmail;
